/**
 * Capture the focused control when an overlay opens; restore it on close
 * when still mounted (S32 / Phase 9). Skip restore after route navigation —
 * callers pass `restoreFocus: false` so natural page focus wins.
 *
 * Plain factory (no Vue inject) — safe to share one instance across
 * `useCatalogMenu` callers that share `useState` open flag.
 */
export function createFocusRestore() {
  let opener: HTMLElement | null = null

  function capture(el?: HTMLElement | null) {
    if (!import.meta.client) return

    if (el) {
      opener = el
      return
    }

    const active = document.activeElement
    opener = active instanceof HTMLElement ? active : null
  }

  function restore() {
    if (!import.meta.client) return

    const el = opener
    opener = null
    if (!el) return

    nextTick(() => {
      if (el.isConnected) {
        el.focus()
      }
    })
  }

  function clear() {
    opener = null
  }

  return {
    capture,
    restore,
    clear,
  }
}

/** Per-setup instance for overlays owned by a single component tree. */
export function useFocusRestore() {
  return createFocusRestore()
}
